/**
 * What the source field accepts, and how it says no.
 *
 * Pure and React-free, so the rules a surface enforces before it signs anything are testable on
 * their own — and so the field and the upload hook can both read them without importing each
 * other.
 */

/** Every file type any import surface can take, and the content type each is signed as. */
const EXTENSION_TO_MIME = { csv: 'text/csv', json: 'application/json' } satisfies Record<string, string>;
export type AcceptedExtension = keyof typeof EXTENSION_TO_MIME;
/** What a surface takes unless it says otherwise. Collections reads both; CSV imports read one. */
export const DEFAULT_ACCEPTED_EXTENSIONS: readonly AcceptedExtension[] = ['csv', 'json'];

/**
 * The drop zone names the file types this surface takes, rather than every type any surface
 * takes: a CSV import's pipeline reads CSV only, so offering JSON there names a file it would
 * sign, upload and then fail on.
 */
export function fileInputLabel(acceptedExtensions: readonly AcceptedExtension[]): string {
  return `Drag and drop ${namedExtensions(acceptedExtensions)} file here`;
}

function namedExtensions(acceptedExtensions: readonly AcceptedExtension[]): string {
  return acceptedExtensions.map((extension) => extension.toUpperCase()).join(' or ');
}

function fileExtension(filename: string): string {
  return filename.split('.').pop()?.toLowerCase() ?? '';
}

/**
 * The limit as the embedded app labels it: binary megabytes, rounded
 * (`import-csv/upload-file/index.gts:91-93`).
 *
 * Rounding can advertise a megabyte the field then rejects, for a limit that is not a whole
 * number of them — but the same limit has to read the same in both apps while both are serving
 * this screen, and every limit either app sets is whole. Sub-megabyte limits drop to KB, where
 * the embedded app would render a bogus "0 MB".
 */
export function maxFileSizeLabel(maxFileSizeBytes: number): string {
  if (maxFileSizeBytes < 1024 * 1024) {
    return `${Math.round(maxFileSizeBytes / 1024)} KB`;
  }

  return `${Math.round(maxFileSizeBytes / 1024 / 1024)} MB`;
}

/** A file this surface can take, or the reason it cannot. */
export type FileCheck = { ok: true; contentType: string } | { ok: false; error: string };

/**
 * Whether a chosen file can be signed and uploaded at all.
 *
 * Every rejection here is one the field answers itself, before the sign request: a file type the
 * surface's pipeline cannot read, a file of no bytes, or one over the workspace's limit. The
 * accepted extension is returned as its content type, so the caller's lookup is total.
 */
export function checkFile(
  file: File,
  acceptedExtensions: readonly AcceptedExtension[],
  maxFileSizeBytes: number | undefined,
): FileCheck {
  // Matched against the accepted list rather than tested for membership, so the extension that
  // carries on is one of the map's own keys.
  const extension = acceptedExtensions.find((accepted) => accepted === fileExtension(file.name));
  if (extension == null) {
    return { ok: false, error: `Choose a ${namedExtensions(acceptedExtensions)} file.` };
  }
  // A file of no bytes has nothing to sign, upload or read, so it is refused at the field rather
  // than sent to a pipeline that would report it as a failed import.
  if (file.size === 0) {
    return { ok: false, error: 'File is empty. Choose at least one row.' };
  }
  // Only reject once the workspace limit is known. Standing in a default here would cap uploads
  // below what the workspace actually allows whenever the settings request is still in flight or
  // has failed; the sign endpoint enforces the real limit either way.
  if (maxFileSizeBytes != null && file.size > maxFileSizeBytes) {
    return { ok: false, error: `File is too large. The maximum size is ${maxFileSizeLabel(maxFileSizeBytes)}.` };
  }

  return { ok: true, contentType: EXTENSION_TO_MIME[extension] };
}
